import React, { useState, useMemo } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { useIntl } from 'umi';
import { Justify, SearchBox } from 'tea-component';
import CardSlot from '@/pages/intelligence/components/CardSlot';
import FixedSubTable from '@/pages/intelligence/components/TableComponents/FixedSubTable';
import CloumsCenter from '@/pages/intelligence/components/TableComponents/components/CloumsCenter';
import SortBy from '@/pages/intelligence/components/TableComponents/components/SortBy';
import SubTable from './SubTable';
import RankTable from './RankTable';
import useTables from './hooks/useTables';
import useSubTableWidth from './hooks/useSubTableWidth';
import { getDynamicColumns } from './utils';
import { COMMON_DATE_FORMATTER } from '@/pages/intelligence/pages/TopCharts/_config/topChartsConst';
import { useDeepCompareEffect } from 'ahooks';
import type { IBaseProps } from '@/pages/intelligence/types/common';
import type {
  IStoreRankDetailsTablesSourceData,
  IStoreRankDetailsCountryRank,
} from '@/pages/intelligence/types/storeRank';

const dateColumnInnerWidth = 170;

export interface ITablesOption {
  showRankName?: boolean;
  /** 是否展示搜索框，默认 true */
  showSearch?: boolean;
  platformIcon?: (dimension: string) => string;
  platformIconTips?: (dimension: string) => React.ReactElement;
  isExpandable?: boolean;
  expandTableRanks?: IStoreRankDetailsCountryRank[];
  sourceDataKey?: string;
  sourceData: IStoreRankDetailsTablesSourceData[];
  sourceDataDimensions: string[];
  sourceDataDimensionNameFormatter?: (name: string) => string;
  gameName?: string;
  gameCover?: string;
  dateType?: string;
  startDate?: string;
  endDate?: string;
}

interface ITablesProps extends ITablesOption, IBaseProps {
  isLoading: boolean;
  isNewOpenTable?: boolean;
  pageLogConfig?: any;
  downloadBtn?: React.ReactElement;
}

export default function Tables(props: ITablesProps) {
  const {
    isLoading,
    platformIcon,
    platformIconTips,
    showSearch = true,
    isExpandable = false,
    expandTableRanks,
    sourceDataKey = 'date',
    sourceData: sourceDataOuter,
    sourceDataDimensions,
    sourceDataDimensionNameFormatter,
    gameName,
    gameCover,
    dateType,
    startDate,
    endDate,
    pageLogConfig,
    downloadBtn,
    isNewOpenTable = false,
    entityType,
  } = props;

  const { formatMessage } = useIntl();

  const dateText = formatMessage({ id: 'dbi_date' });

  const {
    // 搜索后的数据
    sourceData,
    // 排序、分页后的数据（排序、分页 是基于 搜索后的数据 计算的）也是 表格展示的数据
    records,
    // 分页
    pagination,
    // 分页change回调
    handlePaginationChange,
    // 排序
    sortInfo,
    // 排序回调
    handleSortChange,
    // 搜索的关键字
    keywords,
    // 搜索输入框的change回调
    onChange,
    // 搜索的回调
    onSearch,
    // 搜索清空的回调
    onClear,
  } = useTables({
    // 完成的列表数据，表格的分页、排序、搜索 都是基于这个完整的数据 进行计算的
    sourceData: sourceDataOuter,
    pageLogConfig,
  });

  // console.log('TablesTables', props);

  const { subTableWidth, observeTableSize } = useSubTableWidth();

  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  useDeepCompareEffect(() => {
    if (isNewOpenTable) {
      setExpandedKeys([String(records[0]?.[sourceDataKey])]);
    } else {
      setExpandedKeys([]);
    }
  }, [records, isLoading, isNewOpenTable]);

  const dynamicColumns = getDynamicColumns({
    platformIcon,
    platformIconTips,
    dimensions: sourceDataDimensions,
    dimensionFormatter: sourceDataDimensionNameFormatter,
    entityType,
    gameName,
    gameCover,
    dateType,
    startDate,
    endDate,
    sortKey: sortInfo?.key,
    sortValue: sortInfo?.sortVal,
    onSortChage: handleSortChange,
  });

  const isOnlyOneSourceDataDimention = sourceDataDimensions?.length === 1;

  const getCustomContent = () => {
    const tempDom = {
      key: 'date',
      align: 'left',
      width: isOnlyOneSourceDataDimention ? '50%' : dateColumnInnerWidth,
      header: (
        <CloumsCenter width={dateColumnInnerWidth} align="left">
          <SortBy
            align="left"
            title={dateText}
            defaultKey={sortInfo?.key}
            defaultValue={sortInfo?.sortVal}
            list={[
              {
                key: `date_0`,
                ratioType: 0,
                sortMetric: 'date',
                text: dateText,
                title: dateText,
              },
            ]}
            onChange={handleSortChange}
          />
        </CloumsCenter>
      ),
      render: (cvm: IStoreRankDetailsTablesSourceData) => {
        const date = cvm?.date?.trim();
        const dateM = moment(date);
        const dateStr = date && dateM.isValid() ? dateM.format(COMMON_DATE_FORMATTER) : '-';
        return (
          <CloumsCenter width={dateColumnInnerWidth} align="left">
            {dateStr}
          </CloumsCenter>
        );
      },
    };
    if (isNewOpenTable) {
      return [
        tempDom,
        {
          key: '',
          width: 'auto',
          header: '',
        },
      ];
    }
    return [tempDom, ...dynamicColumns];
  };

  return (
    <>
      <Justify
        className="trend-justify"
        right={
          <>
            {showSearch && (
              <SearchBox
                className="trend-justify-search"
                placeholder={formatMessage({ id: 'dbi_store_rank_tables_search_placeholder' })}
                value={keywords}
                onChange={onChange}
                onSearch={onSearch}
                onClear={onClear}
              />
            )}
            {downloadBtn && <div>{downloadBtn}</div>}
          </>
        }
      />
      <CardSlot iconList={[]}>
        {/* sub-table-fixed 子表格固定的类名 组件库修改 全局通用 */}
        {sourceDataOuter?.length && (
          <FixedSubTable
            className={classnames('dbi-store-rank-details_table', {
              'no-sub-table': !isExpandable,
            })}
            loading={isLoading}
            observeTableSize={observeTableSize}
            recordKey={sourceDataKey}
            records={records}
            tableTotal={sourceData?.length}
            pageIndex={pagination?.pageIndex}
            pageSize={pagination?.pageSize}
            pageSizeOptions={pagination?.pageSizes}
            onPagingChange={handlePaginationChange}
            columns={getCustomContent()}
            isExpandable={isExpandable}
            expandableAddon={{
              expandedKeys,
              onExpandedKeysChange: (keys: string[], { event }: any) => {
                event?.stopPropagation();
                setExpandedKeys(keys);
              },
              shouldRecordExpandable: (record: IStoreRankDetailsTablesSourceData) => {
                // 先判断此条数据是否含有noDataRecord字段，如果有，则需要判断是否有数据，无数据则不显示下拉展开按钮
                if (record?.hasOwnProperty('noDataRecord')) {
                  if (entityType === 'combine' || entityType === 'mobile') {
                    return true;
                  }
                  return !record?.noDataRecord;
                }
                true;
              },
              render(record: IStoreRankDetailsTablesSourceData) {
                return (
                  <div
                    style={{
                      width: subTableWidth || 'auto',
                    }}
                  >
                    {isNewOpenTable ? (
                      <RankTable record={record} ranks={expandTableRanks} entityType={entityType} />
                    ) : (
                      <SubTable record={record} ranks={expandTableRanks} />
                    )}
                  </div>
                );
              },
            }}
          />
        )}
      </CardSlot>
    </>
  );
}
